import type {
  MdsScoreOption,
  MdsWorksheetColumn,
  MdsWorksheetSection,
} from 'src/services/documentService/mdsWorksheet';

/**
 * BRK-1852: pure display derivations for the worksheet page — value text,
 * source badges (with the out-of-observation-window warning), section
 * completion fractions, anchor labels. Tested directly.
 */

export const shortDate = (iso: string | null): string => {
  if (!iso) {
    return '—';
  }
  const [year, month, day] = iso.split('-').map(Number);
  return new Date(year, (month || 1) - 1, day || 1).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
};

/** "04 — Supervision or Touching Assistance", or em-dash when unscored */
export const valueDisplay = (
  column: MdsWorksheetColumn | undefined,
  options: MdsScoreOption[] | undefined
): string => {
  if (!column?.currentValue) {
    return '—';
  }
  const match = options?.find((option) => option.value === column.currentValue);
  return match ? `${column.currentValue} — ${match.description}` : column.currentValue;
};

export type SourceBadge = {
  label: string;
  outOfWindow: boolean;
};

/**
 * BRK-1898: "Jane Doe (DOR)" when the backend resolved the actor's name,
 * plain role when it couldn't (deleted user, system writes). Every surface
 * that attributes a change (badges, audit drawer, version history, conflict
 * banner, header chip, patient list) renders through this.
 */
export const actorLabel = (
  name: string | null | undefined,
  role: string | null | undefined
): string => {
  const roleLabel = role || 'Unknown role';
  return name ? `${name} (${roleLabel})` : roleLabel;
};

const DOCUMENT_TYPE_LABELS: Record<string, string> = {
  EVALUATION: 'Eval',
  DISCHARGE: 'Discharge',
  RECERTIFICATION: 'Recert',
  PROGRESS_NOTE: 'Progress',
  DAILY_SESSION_NOTE: 'DSN',
};

/** short label for an MDS document type ("EVALUATION" → "Eval") */
export const docTypeLabel = (documentType: string | null): string =>
  documentType ? DOCUMENT_TYPE_LABELS[documentType] || documentType : 'Document';

/**
 * BRK-1861: a historical value's label for the audit drawer. A null value is
 * an explicit clear; otherwise join the code with its catalog description,
 * falling back to the bare code.
 */
export const scoreValueLabel = (
  value: string | null,
  options: MdsScoreOption[] | undefined
): string => {
  if (value === null || value === '') {
    return 'Cleared';
  }
  const match = options?.find((option) => option.value === value);
  return match ? `${value} — ${match.description}` : value;
};

/** BRK-1861: provenance line for one audit entry */
export const submissionSourceLabel = (
  sourceType: string | null,
  documentType: string | null,
  dateOfService: string | null
): string => {
  // BRK-1866: MDS_COLLECTED (Section K) shows the same document provenance as GG
  if (sourceType === 'GG_COLLECTED' || sourceType === 'MDS_COLLECTED') {
    return `${docTypeLabel(documentType)} · ${shortDate(dateOfService)}`;
  }
  if (sourceType === 'CARRIED_FORWARD') {
    return 'Carried forward';
  }
  return 'Manual';
};

/**
 * `Eval · Jun 28` for document-sourced values, `Manual · Jane Doe (OT) · Jun 30`
 * for hand-entered ones (role-only when the name didn't resolve, BRK-1898);
 * flags out-of-window document sources so the UI can render the amber warning
 * outline (contract: warn, never block).
 */
export const sourceBadge = (column: MdsWorksheetColumn | undefined): SourceBadge | null => {
  if (!column?.currentValue) {
    return null;
  }
  // BRK-1866: MDS_COLLECTED (Section K) shares GG_COLLECTED's document-provenance
  // badge — both carry documentType/dateOfService/inObservationWindow on source
  if (
    (column.sourceType === 'GG_COLLECTED' || column.sourceType === 'MDS_COLLECTED') &&
    column.source
  ) {
    return {
      label: `${docTypeLabel(column.source.documentType)} · ${shortDate(
        column.source.dateOfService
      )}`,
      outOfWindow: column.source.inObservationWindow === false,
    };
  }
  const actor = actorLabel(column.enteredByName, column.enteredByRole || 'Unknown');
  const when = column.enteredAt
    ? ` · ${new Date(column.enteredAt).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
      })}`
    : '';
  return { label: `Manual · ${actor}${when}`, outOfWindow: false };
};

/** the gg_collected levels populated on a candidate, as apply options (BRK-1855) */
export type MdsCandidateLevel = { levelType: string; label: string; value: string };

export const candidateLevels = (candidate: {
  currentLevel: string | null;
  priorLevel: string | null;
  dischargeGoal: string | null;
}): MdsCandidateLevel[] => {
  const levels: MdsCandidateLevel[] = [];
  if (candidate.currentLevel) {
    levels.push({ levelType: 'CURRENT', label: 'Current', value: candidate.currentLevel });
  }
  if (candidate.priorLevel) {
    levels.push({ levelType: 'PRIOR', label: 'Prior', value: candidate.priorLevel });
  }
  if (candidate.dischargeGoal) {
    levels.push({
      levelType: 'DISCHARGE_GOAL',
      label: 'Discharge goal',
      value: candidate.dischargeGoal,
    });
  }
  return levels;
};

export type MdsDocApplyItem = {
  sectionCode: string;
  itemCode: string;
  mdsColumn: string;
  ggCollectedId: number;
  value: string;
};

export type MdsDocGroup = {
  formSubmissionUuid: string;
  documentType: string | null;
  discipline: string | null;
  dateOfService: string | null;
  outOfWindow: boolean;
  items: MdsDocApplyItem[];
};

/**
 * BRK-1855 bulk apply: group the editable sections' gg candidates into source
 * documents, each carrying the Current-level value it offers per item. Only
 * editable sections and candidates with a populated currentLevel participate
 * (the bulk path applies Current performance — the per-item picker handles
 * Prior / Discharge-goal). Newest document first.
 */
export const collectApplyDocuments = (
  sections: MdsWorksheetSection[] | undefined
): MdsDocGroup[] => {
  const groups = new Map<string, MdsDocGroup>();
  (sections || [])
    .filter((section) => section.canEdit)
    .forEach((section) => {
      section.items.forEach((item) => {
        const column = item.columns[0];
        if (!column) {
          return;
        }
        item.ggCandidates.forEach((candidate) => {
          if (!candidate.formSubmissionUuid || !candidate.currentLevel) {
            return;
          }
          let group = groups.get(candidate.formSubmissionUuid);
          if (!group) {
            group = {
              formSubmissionUuid: candidate.formSubmissionUuid,
              documentType: candidate.documentType,
              discipline: candidate.discipline,
              dateOfService: candidate.dateOfService,
              outOfWindow: candidate.inObservationWindow === false,
              items: [],
            };
            groups.set(candidate.formSubmissionUuid, group);
          }
          group.items.push({
            sectionCode: section.sectionCode,
            itemCode: item.itemCode,
            mdsColumn: column.mdsColumn,
            ggCollectedId: candidate.ggCollectedId,
            value: candidate.currentLevel,
          });
        });
      });
    });
  return Array.from(groups.values()).sort((a, b) =>
    (b.dateOfService || '').localeCompare(a.dateOfService || '')
  );
};

/** scored / total across the single applicable entry column per item */
export const sectionCompletion = (
  section: MdsWorksheetSection
): { scored: number; total: number } => {
  const total = section.items.length;
  const scored = section.items.filter((item) =>
    item.columns.some((column) => column.currentValue != null && column.currentValue !== '')
  ).length;
  return { scored, total };
};

/**
 * BRK-1856: draft-aware completeness across EDITABLE sections only, for the
 * submit preview. Distinct from {@link sectionCompletion} (which measures
 * server-saved state per accordion): this reads the caller-supplied value
 * getter, so it reflects the current unsaved draft.
 */
export const editableCompletion = (
  sections: MdsWorksheetSection[] | undefined,
  getValue: (itemCode: string, mdsColumn: string) => string
): { scored: number; total: number } => {
  let scored = 0;
  let total = 0;
  (sections || [])
    .filter((section) => section.canEdit)
    .forEach((section) => {
      section.items.forEach((item) => {
        const column = item.columns[0];
        if (!column) {
          return;
        }
        total += 1;
        if (getValue(item.itemCode, column.mdsColumn) !== '') {
          scored += 1;
        }
      });
    });
  return { scored, total };
};

/** the highest-numbered submission (latest), independent of array order */
export const latestSubmissionVersion = <T extends { versionNumber: number }>(
  versions: T[] | undefined
): T | undefined =>
  (versions || []).reduce<T | undefined>(
    (max, version) => (!max || version.versionNumber > max.versionNumber ? version : max),
    undefined
  );

/** "OBRA_QUARTERLY → OBRA Quarterly", legacy raw strings pass through */
export const assessmentTypeLabel = (type: string | null, raw: string | null): string => {
  if (!type) {
    return raw || 'Assessment';
  }
  const labels: Record<string, string> = {
    OBRA_ADMISSION: 'OBRA Admission',
    OBRA_QUARTERLY: 'OBRA Quarterly',
    OBRA_ANNUAL: 'OBRA Annual',
    SIGNIFICANT_CHANGE: 'Significant Change',
    PPS_5_DAY: '5-Day PPS',
    IPA: 'IPA',
    OBRA_DISCHARGE: 'OBRA Discharge',
  };
  return labels[type] || type;
};

/** header chip: days until ard+14, phrased for humans */
export const completeByLabel = (
  completeByDate: string | null,
  today: Date = new Date()
): { label: string; color: 'default' | 'warning' | 'error' } | null => {
  if (!completeByDate) {
    return null;
  }
  const [year, month, day] = completeByDate.split('-').map(Number);
  const due = new Date(year, (month || 1) - 1, day || 1);
  const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const days = Math.round((due.getTime() - startOfToday.getTime()) / (24 * 60 * 60 * 1000));
  if (days < 0) {
    return { label: `Completion overdue by ${-days}d`, color: 'error' };
  }
  if (days <= 7) {
    return { label: `Complete by ${shortDate(completeByDate)} — ${days}d`, color: 'warning' };
  }
  return { label: `Complete by ${shortDate(completeByDate)}`, color: 'default' };
};
